import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { RulesWithDeviceType, Version } from '@services/types';
import { Button, Card, Col, Form, Row, Typography } from 'antd';
import { find, map } from 'lodash';
import React from 'react';
import VersionSelect from './VersionSelect';
import { FormInstance } from 'antd/es';

const findVersionById = (versionId: string, list: any[]) => {
  return find(list, { id: versionId })?.version;
};

function parseVersion(ver: string): { numbers: number[]; suffix?: string } {
  const [numPart, suffix] = ver.split('-', 2); // split once at first '-'
  return {
    numbers: numPart.split('.').map((n) => parseInt(n, 10) || 0),
    suffix,
  };
}

function compareVersions(curId: string, nextId: string, list: any[]): number {
  const curVer = findVersionById(curId, list);
  const nextVer = findVersionById(nextId, list);

  if (!curVer || !nextVer) return 0;

  if (curVer === '0.0.0' || nextVer === '0.0.0') return -1;

  const cur = parseVersion(curVer);
  const next = parseVersion(nextVer);

  for (let i = 0; i < Math.max(cur.numbers.length, next.numbers.length); i++) {
    const diff = (cur.numbers[i] || 0) - (next.numbers[i] || 0);
    if (diff !== 0) return diff;
  }

  if (cur.suffix && next.suffix) {
    return cur.suffix.localeCompare(next.suffix);
  }

  if (cur.suffix && !next.suffix) return -1;
  if (!cur.suffix && next.suffix) return 1;

  return 0;
}

function validateChainingRules(rules: any[], deviceVersion: any): boolean {
  const { FW = [], LOADER = [], UI = [] } = deviceVersion ?? {};
  for (let i = 0; i < rules.length - 1; i++) {
    const cur = rules[i];
    const next = rules[i + 1];

    if (
      compareVersions(cur?.loaderVersion, next?.loaderVersion, LOADER) >= 0 ||
      compareVersions(cur?.fwVersion, next?.fwVersion, FW) >= 0 ||
      compareVersions(cur?.uiVersion, next?.uiVersion, UI) >= 0
    ) {
      return false;
    }
  }
  return true;
}

function optionFilterHelper(selected: any, options: any[] = []) {
  return map(options, (opt) => {
    return { ...opt, disabled: selected && compareVersions(selected, opt.id, options) >= 0 };
  });
}

const ChainingRules: React.FC<{
  deviceVersion?: Version;
  deviceTypeId?: string;
  ruleToUpdate?: RulesWithDeviceType;
  form?: FormInstance<RulesWithDeviceType>;
}> = ({ deviceVersion, deviceTypeId, ruleToUpdate, form }) => {
  return (
    <Card style={{ gridArea: 'enableChainingRuleForm', marginBottom: '24px' }}>
      <Form.List name="chainingRules">
        {(fields, { add, remove }) => (
          <Col>
            <Typography.Title level={5}>Chaining Rules</Typography.Title>
            {fields.length > 0 && (
              <Row style={{ width: '100%', marginBottom: 4 }}>
                <Col style={{ flex: 1 }}>
                  <Typography.Text strong>Loader</Typography.Text>
                </Col>
                <Col style={{ flex: 1 }}>
                  <Typography.Text strong>Firmware</Typography.Text>
                </Col>
                <Col style={{ flex: 1 }}>
                  <Typography.Text strong>UI</Typography.Text>
                </Col>
                <Col style={{ width: 110 }} />
              </Row>
            )}

            {fields.map(({ key, name, ...restField }, index) => {
              return (
                <Form.Item noStyle shouldUpdate key={key}>
                  {({ getFieldValue }) => {
                    const chainingRules = getFieldValue('chainingRules') || [];
                    const previousRule = index > 0 ? chainingRules[index - 1] : null;

                    return (
                      <Row style={{ width: '100%', gap: 8 }}>
                        <Row style={{ flex: 1, gap: 8 }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'loaderVersion']}
                            rules={[{ required: true, message: 'Please enter a Loader Version' }]}
                            style={{ maxWidth: 'calc((100% - 60px) / 3)', flex: 1 }}
                          >
                            <VersionSelect
                              versions={optionFilterHelper(previousRule?.loaderVersion, deviceVersion?.LOADER)}
                              deviceTypeId={deviceTypeId || ruleToUpdate?.deviceTypeId}
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'fwVersion']}
                            rules={[{ required: true, message: 'Please enter a FW Version' }]}
                            style={{ maxWidth: 'calc((100% - 60px) / 3)', flex: 1 }}
                          >
                            <VersionSelect
                              versions={optionFilterHelper(previousRule?.fwVersion, deviceVersion?.FW)}
                              deviceTypeId={deviceTypeId || ruleToUpdate?.deviceTypeId}
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'uiVersion']}
                            rules={[{ required: true, message: 'Please enter a UI Version' }]}
                            style={{ maxWidth: 'calc((100% - 60px) / 3)', flex: 1 }}
                          >
                            <VersionSelect
                              versions={optionFilterHelper(previousRule?.uiVersion, deviceVersion?.UI)}
                              deviceTypeId={deviceTypeId || ruleToUpdate?.deviceTypeId}
                            />
                          </Form.Item>
                        </Row>
                        <Button onClick={() => remove(name)}>
                          <DeleteOutlined />
                        </Button>
                      </Row>
                    );
                  }}
                </Form.Item>
              );
            })}
            <Form.Item>
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add Chaining Rule
              </Button>
            </Form.Item>
          </Col>
        )}
      </Form.List>
      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.chainingRules !== cur.chainingRules}>
        {({ getFieldValue }) => {
          const rules = getFieldValue('chainingRules') || [];
          const isValid = validateChainingRules(rules, deviceVersion);
          return !isValid ? (
            <Typography.Text type="danger">
              Chaining Rules are invalid: each Loader, FW, UI must be strictly increasing.
            </Typography.Text>
          ) : null;
        }}
      </Form.Item>
      <Form.Item
        name="chainingRulesValidation"
        hidden
        dependencies={['chainingRules']}
        rules={[
          ({ getFieldValue }) => ({
            validator() {
              const rules = getFieldValue('chainingRules') || [];
              const isValid = validateChainingRules(rules, deviceVersion);

              if (!isValid) {
                return Promise.reject(
                  new Error('Chaining Rules are invalid: each Loader, FW, UI must be strictly increasing.'),
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
      ></Form.Item>
    </Card>
  );
};
export default ChainingRules;
